import { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  useFetchCoordinadorPorcentajes,
  useCreateCoordinadorPorcentaje,
  useUpdateCoordinadorPorcentaje,
  useDeleteCoordinadorPorcentaje,
} from '@/shared/actions/erp/comisiones/comisiones.actions';
import type { CoordinadorPorcentaje } from '@/lib/commissions/types';
import { COORDINADOR_PORCENTAJES_PREDEFINIDOS } from '@/lib/commissions/constants';
import LoadingDialog from './LoadingDialog';

interface CoordinadorPorcentajesFormProps {
  onSaved: () => void;
}

const buildFormState = (porcentaje?: CoordinadorPorcentaje): Partial<CoordinadorPorcentaje> => ({
  id: porcentaje?.id,
  contratos_desde: porcentaje?.contratos_desde ?? 1,
  contratos_hasta: porcentaje?.contratos_hasta ?? 10,
  porcentaje_comision: porcentaje?.porcentaje_comision ?? 10,
  observacion: porcentaje?.observacion ?? '',
});

export default function CoordinadorPorcentajesForm({ onSaved }: CoordinadorPorcentajesFormProps) {
  const { data: porcentajes, isLoading } = useFetchCoordinadorPorcentajes();
  const createMutation = useCreateCoordinadorPorcentaje({ enableSuccessAlert: true });
  const updateMutation = useUpdateCoordinadorPorcentaje({ enableSuccessAlert: true });
  const deleteMutation = useDeleteCoordinadorPorcentaje();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPorcentaje, setEditingPorcentaje] = useState<Partial<CoordinadorPorcentaje> | null>(null);
  const [loadingDialogOpen, setLoadingDialogOpen] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const handleOpenDialog = (porcentaje?: CoordinadorPorcentaje) => {
    setEditingPorcentaje(buildFormState(porcentaje));
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPorcentaje(null);
  };

  const handleSave = async () => {
    if (!editingPorcentaje) return;

    try {
      if (editingPorcentaje.id) {
        await updateMutation.mutateAsync(editingPorcentaje as CoordinadorPorcentaje);
      } else {
        await createMutation.mutateAsync(editingPorcentaje);
      }
      onSaved();
      handleCloseDialog();
    } catch (error) {
      console.error('Error al guardar porcentaje:', error);
    }
  };

  const handleDelete = async (id: number) => {
    if (confirm('¿Estás seguro de eliminar este porcentaje?')) {
      try {
        await deleteMutation.mutateAsync(id);
        onSaved();
      } catch (error) {
        console.error('Error al eliminar porcentaje:', error);
      }
    }
  };

  const handleRestablecer = async () => {
    if (!confirm('¿Estás seguro de restablecer todos los porcentajes a los valores predefinidos? Esto eliminará todos los porcentajes actuales.')) {
      return;
    }

    try {
      setLoadingDialogOpen(true);
      setLoadingProgress(0);

      const totalSteps = (porcentajes?.length || 0) + COORDINADOR_PORCENTAJES_PREDEFINIDOS.length;
      let currentStep = 0;

      // Eliminar todos los porcentajes existentes
      if (porcentajes && porcentajes.length > 0) {
        for (const porcentaje of porcentajes) {
          if (porcentaje.id) {
            await deleteMutation.mutateAsync(porcentaje.id);
            currentStep++;
            setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
          }
        }
      }

      // Crear los porcentajes predefinidos
      for (const porcentaje of COORDINADOR_PORCENTAJES_PREDEFINIDOS) {
        await createMutation.mutateAsync(porcentaje);
        currentStep++;
        setLoadingProgress(Math.round((currentStep / totalSteps) * 100));
      }

      onSaved();
      setLoadingDialogOpen(false);
      setLoadingProgress(0);
    } catch (error) {
      console.error('Error al restablecer porcentajes:', error);
      setLoadingDialogOpen(false);
      setLoadingProgress(0);
    }
  };

  const updateEditingPorcentaje = (updates: Partial<CoordinadorPorcentaje>) => {
    setEditingPorcentaje((prev) => (prev ? { ...prev, ...updates } : prev));
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Porcentajes de Comisión por Contratos
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RestoreIcon />}
            onClick={handleRestablecer}
            size="small"
            color="warning"
          >
            Restablecer
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            size="small"
          >
            Agregar Porcentaje
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          Define los porcentajes de comisión según el número de contratos. Estos porcentajes se aplican a la base neta (total vendido - chargebacks) para calcular la comisión variable de los coordinadores.
        </Typography>
      </Alert>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Contratos Desde</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Contratos Hasta</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Porcentaje (%)</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Observación</TableCell>
              <TableCell sx={{ fontWeight: 600 }} align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {porcentajes && porcentajes.length > 0 ? (
              porcentajes.map((porcentaje) => (
                <TableRow key={porcentaje.id} hover>
                  <TableCell>{porcentaje.contratos_desde}</TableCell>
                  <TableCell>{porcentaje.contratos_hasta}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                        {porcentaje.porcentaje_comision}%
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{porcentaje.observacion || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleOpenDialog(porcentaje)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(porcentaje.id!)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No hay porcentajes configurados. Agrega el primer porcentaje haciendo clic en "Agregar Porcentaje".
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de edición/creación */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingPorcentaje?.id ? 'Editar Porcentaje' : 'Agregar Porcentaje'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Contratos Desde"
              type="number"
              fullWidth
              value={editingPorcentaje?.contratos_desde ?? ''}
              onChange={(e) =>
                updateEditingPorcentaje({ contratos_desde: Number(e.target.value) })
              }
              inputProps={{ min: 1 }}
              required
            />
            <TextField
              label="Contratos Hasta"
              type="number"
              fullWidth
              value={editingPorcentaje?.contratos_hasta ?? ''}
              onChange={(e) =>
                updateEditingPorcentaje({ contratos_hasta: Number(e.target.value) })
              }
              inputProps={{ min: 1 }}
              required
            />
            <TextField
              label="Porcentaje de Comisión (%)"
              type="number"
              fullWidth
              value={editingPorcentaje?.porcentaje_comision ?? ''}
              onChange={(e) =>
                updateEditingPorcentaje({ porcentaje_comision: Number(e.target.value) })
              }
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              helperText="Ejemplo: 10.00 para 10%"
              required
            />
            <TextField
              label="Observación"
              fullWidth
              multiline
              rows={2}
              value={editingPorcentaje?.observacion ?? ''}
              onChange={(e) => updateEditingPorcentaje({ observacion: e.target.value })}
              placeholder="Tramo inicial, medio, alto, etc."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {editingPorcentaje?.id ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Dialog */}
      <LoadingDialog
        open={loadingDialogOpen}
        progress={loadingProgress}
        title="Restableciendo porcentajes..."
        message="Eliminando porcentajes actuales y creando los valores predefinidos."
      />
    </Box>
  );
}
